<script>
  import { onMount, createEventDispatcher } from 'svelte';
  import { selectedJobs, selectedJobForEdit, jobsListStore, liveJobsStore, activeTab } from '../../stores.js';
  import { activeJobs, deleteJob, createJob, updateJob, activateJobLive, fetchCompanies, fetchQuotes, filterAllocationsForDate } from '../../data.js';
  import { getJobCustomerName } from '../CreateJob/jobMaterialUtils.js';
  import { slide, fade } from 'svelte/transition';
  import ChevronDown from 'carbon-icons-svelte/lib/ChevronDown.svelte';
  import DeliveryTruck from 'carbon-icons-svelte/lib/DeliveryTruck.svelte';
  import PlayFilled from 'carbon-icons-svelte/lib/PlayFilled.svelte';
  import Location from 'carbon-icons-svelte/lib/Location.svelte';
  import Layers from 'carbon-icons-svelte/lib/Layers.svelte';
  import Enterprise from 'carbon-icons-svelte/lib/Enterprise.svelte';
  import Edit from 'carbon-icons-svelte/lib/Edit.svelte';
  import TrashCan from 'carbon-icons-svelte/lib/TrashCan.svelte';
  import Add from 'carbon-icons-svelte/lib/Add.svelte';
  import Search from 'carbon-icons-svelte/lib/Search.svelte';
  import Close from 'carbon-icons-svelte/lib/Close.svelte';
  import Calendar from 'carbon-icons-svelte/lib/Calendar.svelte';
  import WarningFilled from 'carbon-icons-svelte/lib/WarningFilled.svelte';
  import PhoneInput from '../../components/PhoneInput.svelte';
  import { formatPhoneNumber, toE164 } from '../../phoneUtils.js';

  const dispatch = createEventDispatcher();

  // Reactive reference to jobs list (uses live real-time telematics when available)
  $: rawJobsList = ($liveJobsStore && $liveJobsStore.length > 0)
    ? $liveJobsStore
    : (($jobsListStore && $jobsListStore.length > 0) 
      ? $jobsListStore 
      : ((activeJobs && activeJobs.jobs) ? activeJobs.jobs : []));

  let companies = [];
  let quotes = [];
  onMount(async () => {
    try {
      const [compRes, quotesRes] = await Promise.allSettled([
        fetchCompanies(),
        fetchQuotes()
      ]);
      if (compRes.status === 'fulfilled') {
        const res = compRes.value;
        companies = Array.isArray(res) ? res : res?.companies || [];
      }
      if (quotesRes.status === 'fulfilled') {
        const qRes = quotesRes.value;
        quotes = Array.isArray(qRes) ? qRes : qRes?.quotes || [];
      }
    } catch (_) {}
  });

  function getDisplayCustomerName(job) {
    return getJobCustomerName(job, quotes, companies);
  }

  let searchQuery = "";

  // Delete modal state
  let jobToDelete = null;
  let isDeleting = false;
  let deleteError = "";

  // Quick Create Job modal state
  let showCreateJobModal = false;
  let isCreating = false;
  let createError = "";
  let newJob = {
    companyName: "",
    street: "",
    suburb: "",
    state: "NSW",
    postcode: "",
    contactName: "",
    contactPhone: "",
    status: "active",
    poNumber: "",
    startDate: new Date().toISOString().split("T")[0],
    uhfChannel: "29",
    materialName: "VENM Sand & Virgin Clay",
    materialTonnage: 500,
    notes: ""
  };

  function resetNewJobForm() {
    newJob = {
      companyName: "",
      street: "",
      suburb: "",
      state: "NSW",
      postcode: "",
      contactName: "",
      contactPhone: "",
      status: "active",
      poNumber: "",
      startDate: new Date().toISOString().split("T")[0],
      uhfChannel: "29",
      materialName: "VENM Sand & Virgin Clay",
      materialTonnage: 500,
      notes: ""
    };
    createError = "";
  }

  // Filtered jobs list: this page should only show active jobs
  $: jobsList = rawJobsList.filter(job => {
    const st = (job.status || "proposed").toLowerCase();
    if (st !== "active") {
      return false;
    }

    // Search query filter
    if (!searchQuery.trim()) return true;
    const term = searchQuery.toLowerCase().trim();
    const cust = (job.customer && typeof job.customer === 'object') ? job.customer : {};
    const cName = (cust.name || (typeof job.customer === 'string' ? job.customer : "")).toLowerCase();
    const jNum = String(job.__job_number__ || job.id || "").toLowerCase();
    const poNum = String(job.po_number || "").toLowerCase();
    const addr = cust.address || job.address || {};
    const street = (addr.street || "").toLowerCase();
    const suburb = (addr.suburb || "").toLowerCase();
    const mats = (job.materials || []).map(m => m.name || "").join(" ").toLowerCase();

    return cName.includes(term) || jNum.includes(term) || poNum.includes(term) || street.includes(term) || suburb.includes(term) || mats.includes(term);
  });

  // Track expanded state for each job
  let expandedJobs = {};

  // Initialize selected jobs with all job IDs if empty or if none of the jobs in the current list are selected
  $: if (jobsList.length > 0 && (!$selectedJobs || $selectedJobs.length === 0 || !jobsList.some(j => $selectedJobs.some(id => String(id) === String(j.id))))) {
    $selectedJobs = jobsList.map(j => j.id);
  }

  // Initialize all jobs as expanded initially
  $: if (jobsList.length > 0 && Object.keys(expandedJobs).length === 0) {
    const initialExpanded = {};
    jobsList.forEach(j => {
      initialExpanded[j.id] = true;
    });
    expandedJobs = initialExpanded;
  }

  // Checkbox helpers
  $: allSelected = jobsList.length > 0 && jobsList.every(j => $selectedJobs.some(id => String(id) === String(j.id)));
  $: isIndeterminate = jobsList.some(j => $selectedJobs.some(id => String(id) === String(j.id))) && !allSelected;

  function toggleSelectAll() {
    if (allSelected) {
      $selectedJobs = [];
    } else {
      $selectedJobs = jobsList.map(j => j.id);
    }
    dispatch('job-show');
  }

  function toggleJob(jobId, event) {
    if (event) event.stopPropagation();
    if ($selectedJobs.some(id => String(id) === String(jobId))) {
      $selectedJobs = $selectedJobs.filter(id => String(id) !== String(jobId));
    } else {
      $selectedJobs = [...$selectedJobs, jobId];
    }
    dispatch('job-show');
  }

  function toggleExpand(jobId) {
    expandedJobs[jobId] = !expandedJobs[jobId];
    expandedJobs = { ...expandedJobs };
  }

  function selectForEdit(job, event) {
    if (event) event.stopPropagation();
    $selectedJobForEdit = job;
    dispatch('job-select', { job });
  }

  function promptDelete(job, event) {
    if (event) event.stopPropagation();
    jobToDelete = job;
    deleteError = "";
  }

  function cancelDelete() {
    jobToDelete = null;
    deleteError = "";
  }

  async function confirmDelete() {
    if (!jobToDelete) return;
    isDeleting = true;
    deleteError = "";
    try {
      const deletedId = jobToDelete.id || jobToDelete.__job_number__;
      await deleteJob(deletedId);
      if ($selectedJobForEdit && ($selectedJobForEdit.id === deletedId || $selectedJobForEdit.__job_number__ === deletedId)) {
        $selectedJobForEdit = null;
      }
      jobToDelete = null;
      dispatch('job-show');
      dispatch('job-deleted', { id: deletedId });
    } catch (err) {
      console.error("Failed to delete job:", err);
      deleteError = err.message || "Failed to delete job.";
    } finally {
      isDeleting = false;
    }
  }

  let activatingJobId = null;

  async function handleActivateJob(job, event) {
    if (event && event.stopPropagation) event.stopPropagation();
    const jobId = job.id || job._id || job.__job_number__;
    if (!jobId) return;

    activatingJobId = jobId;
    try {
      console.log(`[JobCards] Activating job #${jobId} to live tracking...`);
      const updated = {
        ...job,
        status: "active",
        materials: (job.materials || []).map(m => ({ ...m, status: "active" }))
      };
      await updateJob(updated);
      await activateJobLive(jobId);
      job.status = "active";
      dispatch('job-show');
      console.log(`[JobCards] Job #${jobId} successfully activated and synced to live database.`);
    } catch (err) {
      console.error(`[JobCards] Failed to activate job #${jobId}:`, err);
      alert(`Failed to activate job: ${err.message || err}`);
    } finally {
      activatingJobId = null;
    }
  }

  async function handleCardStatusChange(job, event) {
    if (event && event.stopPropagation) event.stopPropagation();
    const newStatus = event.target.value;
    const jobId = job.id || job._id || job.__job_number__;
    if (!jobId || !newStatus) return;

    try {
      console.log(`[JobCards] Changing status of job #${jobId} to ${newStatus}...`);
      const updated = {
        ...job,
        status: newStatus,
        materials: (job.materials || []).map(m => ({ ...m, status: newStatus }))
      };
      await updateJob(updated);
      if (newStatus.toLowerCase() === 'active') {
        try {
          await activateJobLive(jobId);
        } catch (liveErr) {
          console.warn(`[JobCards] Live sync on active job #${jobId} failed:`, liveErr);
        }
      }
      job.status = newStatus;
      dispatch('job-show');
    } catch (err) {
      console.error(`[JobCards] Failed to update status of job #${jobId}:`, err);
      alert(`Failed to update status: ${err.message || err}`);
    }
  }

  async function handleQuickCreateJob() {
    if (!newJob.companyName.trim()) {
      createError = "Company / Client Name is required.";
      return;
    }

    isCreating = true;
    createError = "";

    try {
      const clientPhone = newJob.contactPhone.trim() ? toE164(newJob.contactPhone.trim()) : "";
      const clientAddress = {
        street: newJob.street.trim(),
        suburb: newJob.suburb.trim(),
        state: newJob.state.trim() || "NSW",
        postcode: newJob.postcode.trim()
      };
      const clientObj = {
        name: newJob.companyName.trim(),
        phone: clientPhone,
        address: clientAddress
      };

      const tonnage = parseFloat(newJob.materialTonnage) || 500;
      const totalPayload = 38;
      const totalLoads = Math.ceil(tonnage / totalPayload);
      const runsPerDay = 5;
      const estimatedDays = Number((totalLoads / runsPerDay).toFixed(2));

      const payload = {
        customer: clientObj,
        address: clientAddress,
        contacts: [
          {
            name: newJob.contactName.trim() || "Site Supervisor",
            phone: clientPhone,
            email: "",
            position: "Site Contact",
            main: true
          }
        ],
        status: newJob.status,
        po_number: newJob.poNumber.trim(),
        start_date: newJob.startDate,
        days_count: 1,
        load_interval: 15,
        load_uhf_channel: newJob.uhfChannel.trim(),
        dispose_uhf_channel: "",
        site_notes: newJob.notes.trim(),
        notes: newJob.notes.trim(),
        materials: [
          {
            name: newJob.materialName.trim() || "Bulk Material",
            amount: tonnage,
            status: newJob.status,
            start_date: newJob.startDate,
            days_count: 1,
            load_interval: 15,
            load_uhf_channel: newJob.uhfChannel.trim(),
            dispose_uhf_channel: "",
            site_notes: newJob.notes.trim(),
            notes: newJob.notes.trim(),
            yard: null,
            load_address: {
              street: newJob.street.trim(),
              suburb: newJob.suburb.trim(),
              state: newJob.state.trim() || "NSW"
            },
            operating_hours: {
              start_time: "07:00",
              finish_time: "17:00",
              load_mins: 15,
              tip_mins: 15
            },
            fleet: {
              truck_type: "10 wheeler - 19t",
              truck_capacity_tonne: 19,
              trailer_type: "8 wheeler - 20t",
              trailer_capacity_tonne: 19,
              total_capacity_tonne: 38,
              truck_day_rate: 1400
            },
            schedule: {
              runs_per_day: runsPerDay,
              total_loads: totalLoads,
              allocated_trucks: 1,
              estimated_days: estimatedDays,
              total_tolls: 0
            }
          }
        ]
      };

      const created = await createJob(payload);
      showCreateJobModal = false;
      resetNewJobForm();
      dispatch('job-show');
      $selectedJobForEdit = created;
    } catch (err) {
      console.error("Failed to create job:", err);
      createError = err.message || "Failed to create job.";
    } finally {
      isCreating = false;
    }
  }

  function formatNumber(num) {
    if (num === null || num === undefined || isNaN(num)) return "0";
    return Number(num).toLocaleString();
  }

  function getTotalTrucks(job) {
    if (!job.materials) return 0;
    return job.materials.reduce((acc, m) => acc + (m.allocated_trucks ? m.allocated_trucks.length : 0), 0);
  }

  function getTotalTonnes(job) {
    if (!job.materials) return 0;
    return job.materials.reduce((acc, m) => acc + (parseFloat(m.amount) || 0), 0);
  }

  function getStatusTheme(status) {
    switch ((status || "").toLowerCase()) {
      case "active":
        return { label: "Active", bg: "#e6f4ea", color: "#137333", border: "#a8dab5" };
      case "completed":
        return { label: "Completed", bg: "#e8f0fe", color: "#1a73e8", border: "#aecbfa" };
      case "cancelled":
        return { label: "Cancelled", bg: "#fce8e6", color: "#c5221f", border: "#f5c2c7" };
      default:
        return { label: "Proposed", bg: "#fef7e0", color: "#b06000", border: "#fde293" };
    }
  }

  function getTruckRego(t) {
    if (!t) return "TRUCK";
    if (typeof t.truck === "string" && t.truck.trim()) return t.truck.trim();
    if (t.truck && typeof t.truck === "object" && t.truck.id) return String(t.truck.id).trim();
    if (t.rego) return String(t.rego).trim();
    if (t.truck_rego) return String(t.truck_rego).trim();
    if (t.id && typeof t.id === "string") {
      const parts = t.id.split("-");
      if (parts.length > 1 && parts[parts.length - 1].length <= 8) {
        return parts[parts.length - 1];
      }
      return t.id;
    }
    return "TRUCK";
  }

  function getTruckDriver(t) {
    if (!t) return "";
    if (t.driver) {
      if (typeof t.driver === 'string') return t.driver;
      if (t.driver.name) return t.driver.name;
    }
    if (t.truck && typeof t.truck === 'object' && t.truck.default_driver) {
      if (typeof t.truck.default_driver === 'string') return t.truck.default_driver;
      if (t.truck.default_driver.name) return t.truck.default_driver.name;
    }
    return "";
  }

  function getTruckLoadsCount(t) {
    if (!t) return 0;
    if (Array.isArray(t.loads)) {
      return t.loads.length;
    }
    if (typeof t.loads_count === "number") return t.loads_count;
    if (typeof t.load_count === "number") return t.load_count;
    if (typeof t.loads === "number") return t.loads;
    return 0;
  }

  function getTruckDestinationInfo(t, mat) {
    if (!t) return { site: "", suburb: "—", label: "—" };
    const dest = t.destination;
    if (!dest) {
      if (mat && mat.disposal_address && mat.disposal_address.suburb) {
        return { site: "dispose", suburb: mat.disposal_address.suburb, label: `Disposal (${mat.disposal_address.suburb})` };
      }
      if (mat && mat.load_address && mat.load_address.suburb) {
        return { site: "load", suburb: mat.load_address.suburb, label: `Load Site (${mat.load_address.suburb})` };
      }
      return { site: "", suburb: "En Route", label: "En Route" };
    }
    if (typeof dest === 'string') {
      return { site: "", suburb: dest, label: dest };
    }
    if (typeof dest === 'object') {
      const rawSite = (dest.site || "").toLowerCase();
      const siteType = rawSite === 'dispose' ? 'Disposal' : (rawSite === 'load' ? 'Load Site' : (dest.site || ''));
      let suburb = dest.suburb || dest.name || '';

      // If site is load and material specifies the actual load site suburb/address, prioritize it
      if (rawSite === 'load' && mat && mat.load_address) {
        if (mat.load_address.suburb) {
          suburb = mat.load_address.suburb;
        } else if (mat.load_address.street && mat.load_address.street.includes(',')) {
          const parts = mat.load_address.street.split(',').map(s => s.trim()).filter(Boolean);
          if (parts.length >= 2) {
            const sub = parts[1].replace(/\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT|New South Wales|Australia|\d{4})\b/gi, '').trim();
            if (sub) suburb = sub;
          }
        }
      }

      let label = siteType;
      if (siteType && suburb) {
        label = `${siteType} (${suburb})`;
      } else if (suburb) {
        label = suburb;
      }
      return { site: rawSite, suburb: suburb || siteType, label: label || "En Route" };
    }
    return { site: "", suburb: "En Route", label: "En Route" };
  }

  function getJobDisplayLocation(job) {
    if (!job) return { street: "", suburb: "" };
    const firstMat = Array.isArray(job.materials) ? job.materials[0] : null;
    if (firstMat && firstMat.load_address) {
      const la = firstMat.load_address;
      const rawStreet = la.street || la.place_name || la.name || "";
      const suburb = la.suburb || "";
      if (rawStreet) {
        const streetPart = rawStreet.split(',')[0].trim();
        let subPart = suburb;
        if (!subPart && rawStreet.includes(',')) {
          const p1 = rawStreet.split(',')[1].replace(/\b(NSW|VIC|QLD|WA|SA|TAS|ACT|NT|New South Wales|Australia|\d{4})\b/gi, '').trim();
          if (p1) subPart = p1;
        }
        return { street: streetPart || rawStreet, suburb: subPart };
      }
    }
    const custAddr = (job.customer && job.customer.address) ? job.customer.address : (job.address || {});
    return {
      street: custAddr.street || "",
      suburb: custAddr.suburb || ""
    };
  }

  function calculateHaversineKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  function getTruckDistanceText(t, mat) {
    if (!t) return "—";
    let dist = null;
    if (t.destination && typeof t.destination === 'object' && t.destination.distance != null) {
      dist = parseFloat(t.destination.distance);
    } else if (t.distance != null) {
      dist = parseFloat(t.distance);
    } else if (t.distance_km != null) {
      dist = parseFloat(t.distance_km);
    }

    if (dist !== null && !isNaN(dist)) {
      if (dist < 0.1) return "Arrived";
      return `${dist.toFixed(1)} km`;
    }

    // Fallback: Haversine distance
    const truckCoords = t.current_coords || t.coordinates;
    let targetCoords = null;
    if (t.destination && t.destination.coordinates) {
      targetCoords = t.destination.coordinates;
    } else if (mat) {
      const isLoad = t.destination && t.destination.site === 'load';
      const addr = isLoad ? mat.load_address : (mat.disposal_address || mat.load_address);
      if (addr && addr.coordinates) {
        targetCoords = addr.coordinates;
      }
    }

    if (truckCoords && truckCoords.lat != null && truckCoords.lng != null &&
        targetCoords && targetCoords.lat != null && targetCoords.lng != null) {
      const calcDist = calculateHaversineKm(truckCoords.lat, truckCoords.lng, targetCoords.lat, targetCoords.lng);
      if (!isNaN(calcDist)) {
        if (calcDist < 0.1) return "Arrived";
        return `${calcDist.toFixed(1)} km`;
      }
    }

    return "—";
  }

  function getJobTrucks(job) {
    if (!job) return [];
    const trucks = [];
    const seenIds = new Set();

    if (Array.isArray(job.materials)) {
      job.materials.forEach(mat => {
        if (Array.isArray(mat.allocated_trucks)) {
          const matStart = job.start_date || mat.start_date;
          const activeAllocs = filterAllocationsForDate(mat.allocated_trucks, matStart, null, true);
          activeAllocs.forEach(t => {
            const regoKey = getTruckRego(t) || t.id || t._id || (t.current_coords ? `${t.current_coords.lat},${t.current_coords.lng}` : JSON.stringify(t));
            const key = `${mat.id || mat._id || mat.name || 'mat'}_${regoKey}`;
            if (!seenIds.has(key)) {
              seenIds.add(key);
              trucks.push({
                ...t,
                _materialName: mat.name || "Material",
                _material: mat
              });
            }
          });
        }
      });
    }

    if (Array.isArray(job.allocated_trucks)) {
      const activeJobAllocs = filterAllocationsForDate(job.allocated_trucks, job.start_date, null, true);
      activeJobAllocs.forEach(t => {
        const key = getTruckRego(t) || t.id || t._id || (t.current_coords ? `${t.current_coords.lat},${t.current_coords.lng}` : JSON.stringify(t));
        if (!seenIds.has(key)) {
          seenIds.add(key);
          trucks.push({
            ...t,
            _materialName: "General Fleet",
            _material: null
          });
        }
      });
    }

    return trucks;
  }
</script>

<div class="m3-jobs-container">
  <!-- Top Action Toolbar -->
  <div class="m3-top-actions">
    <button 
      class="m3-create-job-btn" 
      type="button" 
      on:click={() => { showCreateJobModal = true; resetNewJobForm(); }}
      title="Create a new haulage job directly"
    >
      <Add size={16} />
      <span>New Job</span>
    </button>

    <div class="m3-live-indicator">
      <span class="pulse-dot"></span>
      <span>Live Tracking</span>
    </div>
  </div>

  <!-- Search Input Bar -->
  <div class="m3-search-bar">
    <Search size={16} class="search-icon" />
    <input 
      type="text" 
      class="search-input" 
      placeholder="Search company, job #, suburb, PO..." 
      bind:value={searchQuery}
    />
    {#if searchQuery}
      <button class="clear-search-btn" type="button" on:click={() => searchQuery = ""} title="Clear search">
        <Close size={14} />
      </button>
    {/if}
  </div>

  <!-- Material 3 Batch Selection / Toolbar Header -->
  <div class="m3-toolbar">
    <div 
      class="m3-select-all-label"
      on:click={toggleSelectAll}
      on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSelectAll(); } }}
      role="button"
      tabindex="0"
    >
      <div 
        class="m3-checkbox {allSelected ? 'checked' : ''} {isIndeterminate ? 'indeterminate' : ''}" 
        role="checkbox"
        aria-checked={allSelected ? "true" : isIndeterminate ? "mixed" : "false"}
        tabindex="-1"
      >
        {#if allSelected}
          <svg class="check-icon" viewBox="0 0 18 18">
            <polyline points="4 9 7.5 12.5 14 5" />
          </svg>
        {:else if isIndeterminate}
          <span class="dash-icon"></span>
        {/if}
      </div>
      <span class="toolbar-title">Select All</span>
      <span class="m3-badge-count">
        {$selectedJobs.length} of {jobsList.length} shown
      </span>
    </div>
  </div>

  <!-- Material 3 Job Cards List -->
  <div class="m3-cards-list">
    {#each jobsList as job (job.id || job.__job_number__)}
      {@const isSelected = $selectedJobs.some(id => String(id) === String(job.id))}
      {@const isExpanded = !!expandedJobs[job.id]}
      {@const isEditing = $selectedJobForEdit && ($selectedJobForEdit.id === job.id || $selectedJobForEdit.__job_number__ === job.__job_number__)}
      {@const totalTrucks = getTotalTrucks(job)}
      {@const totalTonnes = getTotalTonnes(job)}
      {@const theme = getStatusTheme(job.status)}
      {@const loc = getJobDisplayLocation(job)}
      
      <div class="m3-job-card {isSelected ? 'selected' : ''} {isExpanded ? 'expanded' : ''} {isEditing ? 'active-editing' : ''}">
        <!-- Job Card Header (Clickable Accordion Row) -->
        <div 
          class="m3-card-header"
          on:click={() => toggleExpand(job.id)}
          on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(job.id); } }}
          role="button"
          tabindex="0"
        >
          <!-- Material 3 Checkbox -->
          <div 
            class="m3-checkbox {isSelected ? 'checked' : ''}"
            on:click|stopPropagation={(e) => toggleJob(job.id, e)}
            on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleJob(job.id, e); } }}
            role="checkbox"
            aria-checked={isSelected}
            tabindex="0"
            title="Toggle job on map"
          >
            {#if isSelected}
              <svg class="check-icon" viewBox="0 0 18 18">
                <polyline points="4 9 7.5 12.5 14 5" />
              </svg>
            {/if}
          </div>

          <!-- Client Avatar / Icon -->
          <div 
            class="m3-client-avatar" 
            on:click|stopPropagation={(e) => selectForEdit(job, e)} 
            on:keydown={(e) => { if (e.key === 'Enter') selectForEdit(job, e); }}
            role="button"
            tabindex="0"
            title="View / Edit Job Details"
          >
            <Enterprise size={18} />
          </div>

          <!-- Client & Location Info -->
          <div 
            class="m3-client-info" 
            on:click|stopPropagation={(e) => selectForEdit(job, e)} 
            on:keydown={(e) => { if (e.key === 'Enter') selectForEdit(job, e); }}
            role="button"
            tabindex="0"
            title="Click to view/edit job specifications"
          >
            <div class="client-name" title={getDisplayCustomerName(job)}>
              <span>{getDisplayCustomerName(job)}</span>
              {#if job.__job_number__}
                <span class="job-card-badge">#{job.__job_number__}</span>
              {/if}
            </div>
            <div class="client-location">
              <Location size={12} class="loc-icon" />
              <span class="street">{loc.street}</span>
              {#if loc.suburb}
                <span class="loc-divider">•</span>
                <span class="suburb">{loc.suburb}</span>
              {/if}
              {#if job.po_number}
                <span class="loc-divider">•</span>
                <span class="po-tag">PO: {job.po_number}</span>
              {/if}
            </div>
          </div>

          <!-- Summary Badges & Action Buttons -->
          <div class="m3-summary-pills">
            <!-- Status Dropdown (to the left of Activate button) -->
            <select 
              class="m3-job-status-select status-select-{(job.status || 'proposed').toLowerCase()}" 
              value={job.status || 'proposed'}
              on:click|stopPropagation
              on:change={(e) => handleCardStatusChange(job, e)}
              title="Change Job Status"
              aria-label="Change Job Status"
            >
              <option value="proposed">Proposed</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <span class="pill trucks-pill" title="Total allocated fleet">
              <DeliveryTruck size={13} />
              <span>{totalTrucks}</span>
            </span>

            <!-- Activate Button (if not active) -->
            {#if job.status !== 'active'}
              <button 
                class="m3-activate-btn" 
                type="button" 
                title="Activate this job to live tracking"
                disabled={activatingJobId === (job.id || job._id || job.__job_number__)}
                on:click|stopPropagation={(e) => handleActivateJob(job, e)}
              >
                {#if activatingJobId === (job.id || job._id || job.__job_number__)}
                  <span class="btn-spinner-xs"></span>
                  <span>Activating...</span>
                {:else}
                  <PlayFilled size={13} />
                  <span>Activate</span>
                {/if}
              </button>
            {/if}

            <!-- View / Edit Button -->
            <button 
              class="m3-view-edit-btn {isEditing ? 'btn-editing-active' : ''}"
              type="button"
              title="View specifications and edit job details"
              on:click|stopPropagation={(e) => selectForEdit(job, e)}
            >
              <Edit size={13} />
              <span>{isEditing ? "Viewing" : "View"}</span>
            </button>

            <!-- Quick Delete Button -->
            <button 
              class="m3-delete-btn" 
              type="button" 
              title="Delete this job"
              on:click|stopPropagation={(e) => promptDelete(job, e)}
            >
              <TrashCan size={14} />
            </button>
          </div>

          <!-- Expand / Collapse Chevron Button -->
          <button 
            class="m3-expand-btn {isExpanded ? 'rotated' : ''}"
            aria-label={isExpanded ? "Collapse job details" : "Expand job details"}
            type="button"
            on:click|stopPropagation={() => toggleExpand(job.id)}
          >
            <ChevronDown size={18} />
          </button>
        </div>

        <!-- Expanded Content: Active Fleet & Loads Tracking -->
        {#if isExpanded}
          {@const jobTrucks = getJobTrucks(job)}
          {@const totalLoadsDone = jobTrucks.reduce((acc, t) => acc + getTruckLoadsCount(t), 0)}
          <div class="m3-expanded-body" transition:slide={{ duration: 200 }}>
            <div class="m3-subpanel-header">
              <div class="subpanel-title">
                <DeliveryTruck size={15} class="subpanel-icon" />
                <span>Assigned Fleet Tracking</span>
              </div>
              <span class="fleet-summary-badge">
                <span class="fleet-count-tag">{jobTrucks.length} {jobTrucks.length === 1 ? 'Truck' : 'Trucks'}</span>
                <span class="fleet-dot-sep">•</span>
                <span class="loads-count-tag">{totalLoadsDone} {totalLoadsDone === 1 ? 'load done' : 'loads done'}</span>
              </span>
            </div>

            <!-- Trucks Allocation & Live Movements Table -->
            {#if jobTrucks && jobTrucks.length > 0}
              <div class="m3-table-wrapper">
                <table class="m3-trucks-table">
                  <thead>
                    <tr>
                      <th class="col-truck">TRUCK</th>
                      <th class="col-loads">LOADS</th>
                      <th class="col-dest">DESTINATION</th>
                      <th class="col-dist">DISTANCE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each jobTrucks as truck (truck.id || truck._id || truck.truck || truck._materialName)}
                      {@const rego = getTruckRego(truck)}
                      {@const driverName = getTruckDriver(truck)}
                      {@const loadsCount = getTruckLoadsCount(truck)}
                      {@const destInfo = getTruckDestinationInfo(truck, truck._material)}
                      {@const distText = getTruckDistanceText(truck, truck._material)}
                      <tr 
                        class="truck-row" 
                        on:click={() => dispatch('truck-select', { truck, coords: truck.current_coords || truck.coordinates })}
                        title="Click to locate {rego} on map"
                      >
                        <td class="col-truck">
                          <div class="truck-cell">
                            <span class="nsw-plate">{rego}</span>
                            {#if driverName}
                              <span class="truck-driver-sub" title={driverName}>{driverName}</span>
                            {/if}
                          </div>
                        </td>
                        <td class="col-loads">
                          <span class="loads-chip" title="{loadsCount} completed loads">
                            <span class="loads-num">{loadsCount}</span>
                            <span class="loads-unit">{loadsCount === 1 ? 'load' : 'loads'}</span>
                          </span>
                        </td>
                        <td class="col-dest">
                          <div class="dest-cell">
                            {#if destInfo.site === 'dispose'}
                              <span class="dest-tag dest-dispose">Disposal</span>
                            {:else if destInfo.site === 'load'}
                              <span class="dest-tag dest-load">Load Site</span>
                            {/if}
                            <span class="dest-suburb" title={destInfo.label}>{destInfo.suburb}</span>
                          </div>
                        </td>
                        <td class="col-dist">
                          <div class="dist-cell">
                            <span class="dist-num {distText === 'Arrived' ? 'dist-arrived' : ''}">{distText}</span>
                          </div>
                        </td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              </div>
            {:else}
              <div class="no-trucks-note">
                <DeliveryTruck size={20} class="no-trucks-icon" />
                <span>No active trucks assigned to this job yet.</span>
              </div>
            {/if}
          </div>
        {/if}
      </div>
    {:else}
      <div class="m3-empty-state">
        <Enterprise size={32} class="empty-icon" />
        <span class="empty-title">
          {searchQuery ? "No matching active jobs found" : "No active jobs"}
        </span>
        <span class="empty-subtitle">
          {searchQuery ? "Try refining your search terms" : "Create a new job or generate one from Quotes"}
        </span>
        <button 
          class="m3-create-job-btn mt-8" 
          type="button" 
          on:click={() => { showCreateJobModal = true; resetNewJobForm(); }}
        >
          <Add size={16} />
          <span>Create New Job</span>
        </button>
      </div>
    {/each}
  </div>
</div>

<!-- DELETE JOB CONFIRMATION MODAL -->
{#if jobToDelete}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div class="modal-backdrop" role="presentation" on:click={cancelDelete} transition:fade={{ duration: 150 }}>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div class="modal-card" role="dialog" aria-modal="true" on:click|stopPropagation transition:slide={{ duration: 200 }}>
      <div class="modal-header">
        <div class="modal-title-row danger-title">
          <WarningFilled size={20} class="danger-icon" />
          <h3>Delete Job Confirmation</h3>
        </div>
        <button class="modal-close-btn" type="button" on:click={cancelDelete}>
          <Close size={18} />
        </button>
      </div>

      <div class="modal-body">
        <p class="modal-desc">
          Are you sure you want to delete <strong>Job #{jobToDelete.__job_number__ || jobToDelete.id}</strong>
          {#if jobToDelete.customer && jobToDelete.customer.name}
            for <strong>{jobToDelete.customer.name}</strong>
          {:else if typeof jobToDelete.customer === 'string'}
            for <strong>{jobToDelete.customer}</strong>
          {/if}?
        </p>
        <p class="modal-subdesc">
          This operation will delete the Job record and clean up associated materials and vehicle allocations from operations.
        </p>

        {#if deleteError}
          <div class="modal-alert error">
            {deleteError}
          </div>
        {/if}
      </div>

      <div class="modal-footer">
        <button class="modal-btn secondary" type="button" on:click={cancelDelete} disabled={isDeleting}>
          Cancel
        </button>
        <button class="modal-btn danger" type="button" on:click={confirmDelete} disabled={isDeleting}>
          {#if isDeleting}
            <span class="btn-spinner"></span>
            <span>Deleting...</span>
          {:else}
            <TrashCan size={15} />
            <span>Confirm Delete</span>
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- QUICK CREATE JOB MODAL -->
{#if showCreateJobModal}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div class="modal-backdrop" role="presentation" on:click={() => showCreateJobModal = false} transition:fade={{ duration: 150 }}>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div class="modal-card modal-large" role="dialog" aria-modal="true" on:click|stopPropagation transition:slide={{ duration: 200 }}>
      <div class="modal-header">
        <div class="modal-title-row">
          <Add size={20} class="primary-icon" />
          <h3>Create New Job</h3>
        </div>
        <button class="modal-close-btn" type="button" on:click={() => showCreateJobModal = false}>
          <Close size={18} />
        </button>
      </div>

      <div class="modal-body">
        {#if createError}
          <div class="modal-alert error mb-12">
            {createError}
          </div>
        {/if}

        <div class="create-form-grid">
          <!-- Client & Site -->
          <div class="form-group col-12">
            <label class="form-label" for="new-job-company">Client / Company Name *</label>
            <input 
              id="new-job-company"
              type="text" 
              class="modal-input" 
              bind:value={newJob.companyName} 
              placeholder="e.g. Apex Civil Group" 
              required
            />
          </div>

          <div class="form-group col-8">
            <label class="form-label" for="new-job-street">Site Address (Street)</label>
            <input 
              id="new-job-street"
              type="text" 
              class="modal-input" 
              bind:value={newJob.street} 
              placeholder="e.g. 142 Richmond Rd" 
            />
          </div>

          <div class="form-group col-4">
            <label class="form-label" for="new-job-suburb">Suburb</label>
            <input 
              id="new-job-suburb"
              type="text" 
              class="modal-input" 
              bind:value={newJob.suburb} 
              placeholder="e.g. Blacktown" 
            />
          </div>

          <!-- Contact & Reference -->
          <div class="form-group col-6">
            <label class="form-label" for="new-job-contact">Site Contact Person</label>
            <input 
              id="new-job-contact"
              type="text" 
              class="modal-input" 
              bind:value={newJob.contactName} 
              placeholder="e.g. John Doe" 
            />
          </div>

          <div class="form-group col-6">
            <label class="form-label" for="new-job-phone">Contact Phone</label>
            <PhoneInput 
              id="new-job-phone"
              bind:value={newJob.contactPhone} 
              placeholder="0412 345 678" 
            />
          </div>

          <!-- Schedule & UHF -->
          <div class="form-group col-4">
            <label class="form-label" for="new-job-po">Purchase Order #</label>
            <input 
              id="new-job-po"
              type="text" 
              class="modal-input" 
              bind:value={newJob.poNumber} 
              placeholder="e.g. PO-88910" 
            />
          </div>

          <div class="form-group col-4">
            <label class="form-label" for="new-job-date">Start Date</label>
            <input 
              id="new-job-date"
              type="date" 
              class="modal-input" 
              bind:value={newJob.startDate} 
            />
          </div>

          <div class="form-group col-4">
            <label class="form-label" for="new-job-uhf">UHF Channel</label>
            <input 
              id="new-job-uhf"
              type="text" 
              class="modal-input" 
              bind:value={newJob.uhfChannel} 
              placeholder="e.g. 29" 
            />
          </div>

          <!-- Initial Status -->
          <div class="form-group col-6">
            <label class="form-label" for="new-job-status">Initial Status</label>
            <select id="new-job-status" class="modal-input" bind:value={newJob.status}>
              <option value="active">Active (Deploy to Fleet)</option>
              <option value="proposed">Proposed (Draft / Pending)</option>
            </select>
          </div>

          <div class="form-group col-6">
            <label class="form-label" for="new-job-material">Primary Material</label>
            <input 
              id="new-job-material"
              type="text" 
              class="modal-input" 
              bind:value={newJob.materialName} 
              placeholder="Material Name" 
            />
          </div>

          <div class="form-group col-6">
            <label class="form-label" for="new-job-tonnes">Target Quantity (Tonnes)</label>
            <input 
              id="new-job-tonnes"
              type="number" 
              class="modal-input" 
              bind:value={newJob.materialTonnage} 
              min="1" 
              step="50" 
            />
          </div>

          <div class="form-group col-12">
            <label class="form-label" for="new-job-notes">Site Notes / Dispatch Instructions</label>
            <textarea 
              id="new-job-notes"
              class="modal-textarea" 
              rows="3" 
              bind:value={newJob.notes} 
              placeholder="Access gates, PPE rules, tipping dock requirements..."
            ></textarea>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button 
          class="modal-btn secondary" 
          type="button" 
          on:click={() => { showCreateJobModal = false; $activeTab = 'create-job'; }}
          title="Switch to full multi-day allocation board"
        >
          <span>Open Full Allocation Board</span>
        </button>

        <button class="modal-btn secondary" type="button" on:click={() => showCreateJobModal = false} disabled={isCreating}>
          Cancel
        </button>

        <button class="modal-btn primary" type="button" on:click={handleQuickCreateJob} disabled={isCreating}>
          {#if isCreating}
            <span class="btn-spinner"></span>
            <span>Creating...</span>
          {:else}
            <Add size={16} />
            <span>Create Job</span>
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .m3-jobs-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-family: var(--tt-font, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif);
  }

  /* Top Actions & Create Button */
  .m3-top-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .m3-create-job-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background-color: var(--tt-primary, #2563eb);
    color: #ffffff;
    border: none;
    border-radius: var(--tt-radius-md, 8px);
    padding: 7px 12px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.1s ease;
  }

  .m3-create-job-btn:hover {
    background-color: var(--tt-primary-dark, #1d4ed8);
    transform: translateY(-1px);
  }

  .m3-live-indicator {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 600;
    color: #137333;
    background-color: #e6f4ea;
    padding: 3px 8px;
    border-radius: 9999px;
    border: 1px solid #ceead6;
  }

  .pulse-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #137333;
    box-shadow: 0 0 0 0 rgba(19, 115, 51, 0.4);
    animation: m3-pulse 1.8s infinite;
  }

  @keyframes m3-pulse {
    0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(19, 115, 51, 0.6); }
    70% { transform: scale(1); box-shadow: 0 0 0 5px rgba(19, 115, 51, 0); }
    100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(19, 115, 51, 0); }
  }
  /* Search Input Bar */
  .m3-search-bar {
    position: relative;
    display: flex;
    align-items: center;
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: var(--tt-radius-md, 8px);
    padding: 0 8px;
  }

  :global(.search-icon) {
    color: var(--tt-slate-400, #94a3b8);
    flex-shrink: 0;
  }

  .search-input {
    width: 100%;
    padding: 6px 8px;
    border: none;
    font-size: 12.5px;
    color: var(--tt-slate-800, #1e293b);
    outline: none;
    background: transparent;
  }

  .clear-search-btn {
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: var(--tt-slate-400, #94a3b8);
    display: flex;
    align-items: center;
  }

  .clear-search-btn:hover {
    color: var(--tt-slate-700, #334155);
  }

  /* Material 3 Toolbar / Select All Bar */
  .m3-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    background-color: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: var(--tt-radius-md, 8px);
  }

  .m3-select-all-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
  }

  .toolbar-title {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--tt-slate-800, #1e293b);
  }

  .m3-badge-count {
    font-size: 11px;
    font-weight: 600;
    color: var(--tt-primary, #2563eb);
    background-color: rgba(37, 99, 235, 0.08);
    padding: 2px 7px;
    border-radius: 9999px;
  }

  /* Material 3 Checkbox */
  .m3-checkbox {
    width: 18px;
    height: 18px;
    border: 1.5px solid var(--tt-slate-400, #94a3b8);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #ffffff;
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;
  }

  .m3-checkbox:hover {
    border-color: var(--tt-primary, #2563eb);
  }

  .m3-checkbox.checked {
    background-color: var(--tt-primary, #2563eb);
    border-color: var(--tt-primary, #2563eb);
  }

  .m3-checkbox.indeterminate {
    background-color: var(--tt-primary, #2563eb);
    border-color: var(--tt-primary, #2563eb);
  }

  .check-icon {
    width: 12px;
    height: 12px;
    stroke: #ffffff;
    stroke-width: 2.5;
    fill: none;
  }

  .dash-icon {
    width: 8px;
    height: 2px;
    background-color: #ffffff;
    border-radius: 1px;
  }

  /* Job Cards List */
  .m3-cards-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: calc(100vh - 270px);
    overflow-y: auto;
    padding-right: 2px;
  }

  .m3-job-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: var(--tt-radius-md, 8px);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    overflow: hidden;
  }

  .m3-job-card:hover {
    border-color: var(--tt-slate-300, #cbd5e1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  }

  .m3-job-card.selected {
    border-color: rgba(37, 99, 235, 0.35);
  }

  .m3-job-card.active-editing {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 1px var(--tt-primary, #2563eb);
  }

  /* Card Header */
  .m3-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    cursor: pointer;
    user-select: none;
  }

  .m3-client-avatar {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background-color: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-600, #475569);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color 0.15s;
  }

  .m3-client-avatar:hover {
    background-color: var(--tt-slate-200, #e2e8f0);
    color: var(--tt-primary, #2563eb);
  }

  .m3-client-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .client-name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--tt-slate-900, #0f172a);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .job-card-badge {
    font-size: 10.5px;
    font-weight: 700;
    color: var(--tt-primary, #2563eb);
    background-color: rgba(37, 99, 235, 0.08);
    padding: 1px 5px;
    border-radius: 4px;
  }

  .client-location {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    color: var(--tt-slate-500, #64748b);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.loc-icon) {
    color: var(--tt-slate-400, #94a3b8);
    flex-shrink: 0;
  }

  .loc-divider {
    color: var(--tt-slate-300, #cbd5e1);
  }

  .po-tag {
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
  }

  /* Summary Pills & Actions */
  .m3-summary-pills {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
  }

  .m3-job-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10.5px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 9999px;
    text-transform: capitalize;
  }

  .m3-job-status-select {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10.5px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 9999px;
    text-transform: capitalize;
    cursor: pointer;
    outline: none;
    transition: all 0.15s ease;
  }

  .m3-job-status-select.status-select-active {
    background-color: #e6f4ea;
    color: #137333;
    border: 1px solid #a8dab5;
  }

  .m3-job-status-select.status-select-completed {
    background-color: #e8f0fe;
    color: #1a73e8;
    border: 1px solid #aecbfa;
  }

  .m3-job-status-select.status-select-cancelled {
    background-color: #fce8e6;
    color: #c5221f;
    border: 1px solid #f5c2c7;
  }

  .m3-job-status-select.status-select-proposed,
  .m3-job-status-select.status-select-pending {
    background-color: #fef7e0;
    color: #b06000;
    border: 1px solid #fde293;
  }

  .pill-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 6px;
    border-radius: 4px;
    background-color: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
  }

  .m3-activate-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background-color: #16a34a;
    color: #ffffff;
    border: 1px solid #15803d;
    border-radius: 4px;
    padding: 3px 7px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 1px 2px rgba(22, 163, 74, 0.2);
    transition: all 0.15s ease;
  }

  .m3-activate-btn:hover {
    background-color: #15803d;
    border-color: #166534;
  }

  .m3-activate-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  .btn-spinner-xs {
    width: 10px;
    height: 10px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .m3-view-edit-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background-color: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 4px;
    padding: 3px 7px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .m3-view-edit-btn:hover {
    background-color: var(--tt-primary, #2563eb);
    color: #ffffff;
    border-color: var(--tt-primary, #2563eb);
  }

  .btn-editing-active {
    background-color: var(--tt-primary, #2563eb);
    color: #ffffff;
    border-color: var(--tt-primary, #2563eb);
  }

  .m3-delete-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: var(--tt-slate-400, #94a3b8);
    padding: 4px;
    border-radius: 4px;
    cursor: pointer;
    transition: color 0.15s, background-color 0.15s;
  }

  .m3-delete-btn:hover {
    color: #c5221f;
    background-color: #fce8e6;
  }

  .m3-expand-btn {
    background: none;
    border: none;
    padding: 2px;
    color: var(--tt-slate-400, #94a3b8);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease, color 0.15s;
  }

  .m3-expand-btn:hover {
    color: var(--tt-slate-700, #334155);
  }

  .m3-expand-btn.rotated {
    transform: rotate(180deg);
  }

  /* Expanded Body */
  .m3-expanded-body {
    padding: 8px 12px 12px 12px;
    border-top: 1px solid var(--tt-slate-100, #f1f5f9);
    background-color: var(--tt-slate-50, #f8fafc);
  }

  .m3-subpanel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .subpanel-title {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
  }

  .fleet-summary-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 600;
  }

  .fleet-count-tag {
    color: var(--tt-slate-700, #334155);
  }

  .fleet-dot-sep {
    color: var(--tt-slate-300, #cbd5e1);
  }

  .loads-count-tag {
    color: #047857;
    background: #ecfdf5;
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid #a7f3d0;
  }

  .m3-table-wrapper {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    overflow: hidden;
  }

  .m3-trucks-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11.5px;
  }

  .m3-trucks-table th {
    background-color: var(--tt-slate-50, #f8fafc);
    color: var(--tt-slate-500, #64748b);
    font-weight: 600;
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
    font-size: 10.5px;
    letter-spacing: 0.3px;
  }

  .m3-trucks-table td {
    padding: 6px 8px;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-800, #1e293b);
    vertical-align: middle;
  }

  .m3-trucks-table tr:last-child td {
    border-bottom: none;
  }

  .truck-row {
    cursor: pointer;
    transition: background-color 0.15s ease;
  }

  .truck-row:hover {
    background-color: #f1f5f9;
  }

  .col-truck {
    width: 25%;
  }

  .col-loads {
    width: 18%;
  }

  .col-dest {
    width: 37%;
  }

  .col-dist {
    width: 20%;
  }

  .truck-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }

  .nsw-plate {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ffcc00;
    color: #111827;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-weight: 800;
    font-size: 11px;
    letter-spacing: 0.5px;
    padding: 1.5px 6px;
    border-radius: 3px;
    border: 1px solid #1f2937;
    box-shadow: 0 1px 2px rgba(0,0,0,0.12);
    text-transform: uppercase;
    line-height: 1.2;
  }

  .truck-driver-sub {
    font-size: 10.5px;
    color: var(--tt-slate-500, #64748b);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 110px;
    line-height: 1.1;
  }

  .loads-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
    border-radius: 12px;
    padding: 2px 7px;
    font-size: 11px;
  }

  .loads-num {
    font-weight: 700;
  }

  .loads-unit {
    font-size: 10px;
    color: #047857;
  }

  .dest-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  .dest-tag {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    padding: 1px 5px;
    border-radius: 3px;
    line-height: 1.2;
  }

  .dest-dispose {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .dest-load {
    background: #f0fdf4;
    color: #15803d;
    border: 1px solid #bbf7d0;
  }

  .dest-suburb {
    font-size: 11.5px;
    font-weight: 600;
    color: #1e293b;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 155px;
    line-height: 1.2;
  }

  .dist-cell {
    display: flex;
    align-items: center;
  }

  .dist-num {
    font-size: 12px;
    font-weight: 700;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
  }

  .dist-arrived {
    color: #16a34a;
    background: #dcfce7;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10.5px;
    font-weight: 700;
  }

  .no-trucks-note {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px 8px;
    font-size: 12px;
    color: var(--tt-slate-500, #64748b);
    background: #ffffff;
    border: 1px dashed var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
  }

  .no-trucks-icon {
    color: var(--tt-slate-400, #94a3b8);
  }

  /* Empty State */
  .m3-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    background-color: var(--tt-slate-50, #f8fafc);
    border: 1px dashed var(--tt-slate-300, #cbd5e1);
    border-radius: var(--tt-radius-md, 8px);
    text-align: center;
  }

  :global(.empty-icon) {
    color: var(--tt-slate-400, #94a3b8);
    margin-bottom: 8px;
  }

  .empty-title {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
  }

  .empty-subtitle {
    font-size: 12px;
    color: var(--tt-slate-500, #64748b);
    margin-top: 2px;
  }

  .mt-8 { margin-top: 8px; }
  .mb-12 { margin-bottom: 12px; }

  /* Modals */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(15, 23, 42, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 16px;
  }

  .modal-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
    width: 440px;
    max-width: 95vw;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-card.modal-large {
    width: 620px;
    max-height: 90vh;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .modal-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .modal-title-row h3 {
    font-size: 15px;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
    margin: 0;
  }

  .danger-title h3 {
    color: #c5221f;
  }

  :global(.danger-icon) {
    color: #c5221f;
  }

  :global(.primary-icon) {
    color: var(--tt-primary, #2563eb);
  }

  .modal-close-btn {
    background: none;
    border: none;
    color: var(--tt-slate-400, #94a3b8);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-close-btn:hover {
    color: var(--tt-slate-700, #334155);
  }

  .modal-body {
    padding: 16px 18px;
    overflow-y: auto;
  }

  .modal-desc {
    font-size: 13.5px;
    color: var(--tt-slate-800, #1e293b);
    margin: 0 0 8px 0;
    line-height: 1.4;
  }

  .modal-subdesc {
    font-size: 12.5px;
    color: var(--tt-slate-500, #64748b);
    margin: 0;
    line-height: 1.4;
  }

  .modal-alert {
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    margin-top: 10px;
  }

  .modal-alert.error {
    background-color: #fce8e6;
    color: #c5221f;
    border: 1px solid #f5c2c7;
  }

  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 18px;
    border-top: 1px solid var(--tt-slate-200, #e2e8f0);
    background-color: var(--tt-slate-50, #f8fafc);
  }

  .modal-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 6px;
    border: none;
    cursor: pointer;
    transition: all 0.15s;
  }

  .modal-btn.secondary {
    background-color: #ffffff;
    color: var(--tt-slate-700, #334155);
    border: 1px solid var(--tt-slate-300, #cbd5e1);
  }

  .modal-btn.secondary:hover {
    background-color: var(--tt-slate-100, #f1f5f9);
  }

  .modal-btn.primary {
    background-color: var(--tt-primary, #2563eb);
    color: #ffffff;
  }

  .modal-btn.primary:hover {
    background-color: var(--tt-primary-dark, #1d4ed8);
  }

  .modal-btn.danger {
    background-color: #c5221f;
    color: #ffffff;
  }

  .modal-btn.danger:hover {
    background-color: #9f1917;
  }

  .modal-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Create Form Grid */
  .create-form-grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 10px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .col-12 { grid-column: span 12; }
  .col-8 { grid-column: span 8; }
  .col-6 { grid-column: span 6; }
  .col-4 { grid-column: span 4; }

  .form-label {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
  }

  .modal-input {
    padding: 7px 10px;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    font-size: 12.5px;
    color: var(--tt-slate-800, #1e293b);
    outline: none;
    transition: border-color 0.15s;
    background: #ffffff;
  }

  .modal-input:focus {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .modal-textarea {
    padding: 7px 10px;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    font-size: 12.5px;
    color: var(--tt-slate-800, #1e293b);
    outline: none;
    resize: vertical;
    font-family: inherit;
  }

  .modal-textarea:focus {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }
</style>
